/**
 * Automação — redesign moderno
 * Robô eCAC/Carnê Leão + card preparado para integração Claude AI
 */

import React, { useState } from 'react';
import {
  Box, Grid, Typography, Card, CardContent,
  Button, CircularProgress, Alert, Chip, Divider,
} from '@mui/material';
import SmartToyIcon       from '@mui/icons-material/SmartToy';
import PlayArrowIcon      from '@mui/icons-material/PlayArrow';
import StopIcon           from '@mui/icons-material/Stop';
import CheckCircleIcon    from '@mui/icons-material/CheckCircle';
import ErrorIcon          from '@mui/icons-material/Error';
import AutoAwesomeIcon    from '@mui/icons-material/AutoAwesome';
import ReceiptLongIcon    from '@mui/icons-material/ReceiptLong';
import PersonIcon         from '@mui/icons-material/Person';
import SpeedIcon          from '@mui/icons-material/Speed';
import LockClockIcon      from '@mui/icons-material/LockClock';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

/* ─── palette ─── */
const ORANGE  = '#F97316';
const ORANGE_L = '#FB923C';
const BLUE    = '#2563EB';
const GREEN   = '#10B981';
const RED     = '#EF4444';
const AMBER   = '#F59E0B';

/* ─── log de atividade mock ─── */
const LOG_INICIAL = [
  { id: 1, tipo: 'info',    msg: 'Sistema de automação inicializado',         hora: '11:30' },
  { id: 2, tipo: 'info',    msg: 'Aguardando comando para iniciar robô',       hora: '11:30' },
];

/* ─── Feature card (em breve) ─── */
const FeatureCard = ({ icon: Icon, title, desc, badge, color }) => (
  <Box sx={{
    display: 'flex', alignItems: 'flex-start', gap: 1.5,
    p: 1.8, borderRadius: 2,
    bgcolor: `${color}08`,
    border: `1px solid ${color}18`,
  }}>
    <Box sx={{
      width: 34, height: 34, borderRadius: 2, flexShrink: 0,
      bgcolor: `${color}15`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Icon sx={{ fontSize: 17, color }} />
    </Box>
    <Box sx={{ flex: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1E293B' }}>{title}</Typography>
        {badge && (
          <Chip label={badge} size="small" sx={{ height: 17, fontSize: 9, fontWeight: 700, bgcolor: `${color}15`, color, border: 'none' }} />
        )}
      </Box>
      <Typography sx={{ fontSize: 12, color: '#64748B', lineHeight: 1.5 }}>{desc}</Typography>
    </Box>
  </Box>
);

/* ═══════════════════════════════════════════ */
const RoboAutomacao = () => {
  const { token } = useAuth();
  const [ativo,    setAtivo]    = useState(false);
  const [erro,     setErro]     = useState(null);
  const [mensagem, setMensagem] = useState('');
  const [loading,  setLoading]  = useState(false);
  const [usuario,  setUsuario]  = useState('');
  const [lancamento, setLancamento] = useState('');
  const [progresso,  setProgresso]  = useState('');
  const [log, setLog] = useState(LOG_INICIAL);

  const addLog = (tipo, msg) => {
    const hora = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    setLog(prev => [{ id: Date.now(), tipo, msg, hora }, ...prev].slice(0, 20));
  };

  const iniciarRobo = async () => {
    setLoading(true); setErro(null); setMensagem('');
    addLog('info', 'Iniciando robô eCAC/Carnê Leão...');
    try {
      const r = await axios.post('/api/robo/processar-lancamento-unico', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMensagem(r.data.mensagem);
      setAtivo(true);
      addLog('success', r.data.mensagem || 'Robô iniciado com sucesso');
      checarStatus();
    } catch (err) {
      const msg = err.response?.data?.mensagem || 'Erro ao iniciar robô';
      setErro(msg);
      addLog('error', msg);
    } finally {
      setLoading(false);
    }
  };

  const checarStatus = () => {
    const interval = setInterval(async () => {
      try {
        const res = await axios.get('/api/robo/status-robo');
        setAtivo(res.data.ativo);
        setMensagem(res.data.mensagem);
        setUsuario(res.data.usuario_atual || '');
        setLancamento(res.data.lancamento_atual || '');
        setProgresso(res.data.progresso || '');
        if (res.data.mensagem) addLog('info', res.data.mensagem);
        if (res.data.erro) { setErro(res.data.erro); addLog('error', res.data.erro); }
        if (!res.data.ativo) {
          clearInterval(interval);
          addLog('success', 'Robô finalizado com sucesso');
        }
      } catch {
        setErro('Erro ao consultar status');
        addLog('error', 'Falha na comunicação com o robô');
        clearInterval(interval);
      }
    }, 3000);
  };

  const logColor = { info: BLUE, success: GREEN, error: RED, warn: AMBER };
  const logBg    = { info: 'rgba(37,99,235,0.06)', success: 'rgba(16,185,129,0.06)', error: 'rgba(239,68,68,0.06)', warn: 'rgba(245,158,11,0.06)' };

  return (
    <Box sx={{ pb: 4 }}>

      {/* ── Hero ── */}
      <Box sx={{
        mb: 3, p: 3, borderRadius: 3,
        background: 'linear-gradient(135deg, #0F172A 0%, #1E3A5F 60%, #0C2340 100%)',
        position: 'relative', overflow: 'hidden',
      }}>
        <Box sx={{ position: 'absolute', right: -20, top: -20, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(249,115,22,0.07)' }} />
        <Box sx={{ position: 'absolute', right: 120, bottom: -50, width: 110, height: 110, borderRadius: '50%', bgcolor: 'rgba(37,99,235,0.06)' }} />

        <Box sx={{ position: 'relative' }}>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#fff', mb: 0.3 }}>
            Central de Automação
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'rgba(147,197,253,0.85)' }}>
            Gerencie robôs e automações inteligentes da plataforma
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mt: 2.5, flexWrap: 'wrap' }}>
            {[
              { label: 'Robôs ativos',     val: ativo ? '1' : '0' },
              { label: 'Disponíveis',      val: '1'               },
              { label: 'Em desenvolvimento', val: '2'             },
            ].map((s) => (
              <Box key={s.label} sx={{
                px: 2, py: 0.9,
                bgcolor: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 2,
              }}>
                <Typography sx={{ fontSize: 10, color: 'rgba(147,197,253,0.7)', lineHeight: 1 }}>{s.label}</Typography>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>{s.val}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Grid container spacing={2.5}>

        {/* ── Card Robô Carnê Leão ── */}
        <Grid item xs={12} md={5}>
          <Card elevation={0} sx={{
            borderRadius: 3, border: '1px solid #E2E8F0', bgcolor: '#fff',
            height: '100%', overflow: 'hidden',
          }}>
            <Box sx={{ height: 4, background: ativo
              ? `linear-gradient(90deg, ${GREEN} 0%, #34D399 100%)`
              : `linear-gradient(90deg, ${ORANGE} 0%, ${ORANGE_L} 100%)` }} />

            <CardContent sx={{ p: 3 }}>
              {/* cabeçalho */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{
                    width: 56, height: 56, borderRadius: 3,
                    background: ativo
                      ? `linear-gradient(135deg, ${GREEN} 0%, #34D399 100%)`
                      : `linear-gradient(135deg, ${ORANGE} 0%, ${ORANGE_L} 100%)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: ativo
                      ? '0 4px 14px rgba(16,185,129,0.4)'
                      : '0 4px 14px rgba(249,115,22,0.4)',
                    position: 'relative',
                  }}>
                    <SmartToyIcon sx={{ color: '#fff', fontSize: 28 }} />
                    {ativo && (
                      <Box sx={{
                        position: 'absolute', top: -3, right: -3,
                        width: 14, height: 14, borderRadius: '50%',
                        bgcolor: GREEN, border: '2px solid #fff',
                        animation: 'pulse 1.5s infinite',
                        '@keyframes pulse': {
                          '0%':   { boxShadow: `0 0 0 0 rgba(16,185,129,0.5)` },
                          '70%':  { boxShadow: `0 0 0 6px rgba(16,185,129,0)` },
                          '100%': { boxShadow: `0 0 0 0 rgba(16,185,129,0)` },
                        },
                      }} />
                    )}
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#0F172A' }}>
                      Robô eCAC / Carnê Leão
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>
                      Lançamento automático de receitas PF
                    </Typography>
                  </Box>
                </Box>

                {/* status badge */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                  <Box sx={{
                    width: 8, height: 8, borderRadius: '50%',
                    bgcolor: ativo ? GREEN : '#CBD5E1',
                    ...(ativo && {
                      animation: 'pulse 1.5s infinite',
                      '@keyframes pulse': {
                        '0%':   { boxShadow: `0 0 0 0 rgba(16,185,129,0.5)` },
                        '70%':  { boxShadow: `0 0 0 6px rgba(16,185,129,0)` },
                        '100%': { boxShadow: `0 0 0 0 rgba(16,185,129,0)` },
                      },
                    }),
                  }} />
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: ativo ? GREEN : '#94A3B8' }}>
                    {ativo ? 'Rodando' : 'Inativo'}
                  </Typography>
                </Box>
              </Box>

              {/* info em execução */}
              {ativo && (
                <Box sx={{
                  p: 2, borderRadius: 2, mb: 2.5,
                  bgcolor: 'rgba(16,185,129,0.06)',
                  border: '1px solid rgba(16,185,129,0.15)',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CircularProgress size={14} thickness={4} sx={{ color: GREEN }} />
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: GREEN }}>Processando...</Typography>
                  </Box>
                  {usuario && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.5 }}>
                      <PersonIcon sx={{ fontSize: 13, color: '#94A3B8' }} />
                      <Typography sx={{ fontSize: 12, color: '#64748B' }}>
                        Usuário: <b style={{ color: '#1E293B' }}>{usuario}</b>
                      </Typography>
                    </Box>
                  )}
                  {lancamento && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                      <ReceiptLongIcon sx={{ fontSize: 13, color: '#94A3B8' }} />
                      <Typography sx={{ fontSize: 12, color: '#64748B' }}>
                        Lançamento: <b style={{ color: '#1E293B' }}>{lancamento}</b>
                        {progresso && <span style={{ color: '#94A3B8' }}> ({progresso})</span>}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {mensagem && !ativo && (
                <Box sx={{ p: 1.5, borderRadius: 2, mb: 2, bgcolor: 'rgba(37,99,235,0.06)', border: '1px solid rgba(37,99,235,0.12)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <CheckCircleIcon sx={{ fontSize: 15, color: GREEN }} />
                    <Typography sx={{ fontSize: 12, color: '#1E293B', fontWeight: 500 }}>{mensagem}</Typography>
                  </Box>
                </Box>
              )}

              {erro && (
                <Alert severity="error" icon={<ErrorIcon sx={{ fontSize: 16 }} />}
                  sx={{ mb: 2, borderRadius: 2, fontSize: 12, py: 0.5 }}>
                  {erro}
                </Alert>
              )}

              {/* detalhes */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
                {[
                  { Icon: ReceiptLongIcon, label: 'Função',   val: 'Carnê Leão — eCAC Receita Federal' },
                  { Icon: PersonIcon,      label: 'Escopo',   val: 'Dentistas Pessoa Física (PF)'      },
                  { Icon: SpeedIcon,       label: 'Intervalo',val: 'Verificação a cada 3 segundos'     },
                ].map(({ Icon, label, val }) => (
                  <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: 'rgba(249,115,22,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon sx={{ fontSize: 14, color: ORANGE }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 10, color: '#94A3B8', lineHeight: 1 }}>{label}</Typography>
                      <Typography sx={{ fontSize: 12.5, color: '#1E293B', fontWeight: 500 }}>{val}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* botão */}
              {!ativo ? (
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <PlayArrowIcon sx={{ fontSize: 18 }} />}
                  disabled={loading}
                  onClick={iniciarRobo}
                  sx={{
                    borderRadius: 2, py: 1.2, fontSize: 13.5, fontWeight: 700,
                    textTransform: 'none',
                    background: `linear-gradient(135deg, ${ORANGE} 0%, ${ORANGE_L} 100%)`,
                    boxShadow: '0 3px 10px rgba(249,115,22,0.4)',
                    '&:hover': { boxShadow: '0 5px 16px rgba(249,115,22,0.5)' },
                  }}
                >
                  {loading ? 'Iniciando robô...' : 'Iniciar Robô'}
                </Button>
              ) : (
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<StopIcon sx={{ fontSize: 18 }} />}
                  onClick={() => { setAtivo(false); addLog('warn', 'Robô interrompido manualmente'); }}
                  sx={{
                    borderRadius: 2, py: 1.2, fontSize: 13.5, fontWeight: 700,
                    textTransform: 'none',
                    borderColor: RED, color: RED,
                    '&:hover': { bgcolor: 'rgba(239,68,68,0.06)', borderColor: RED },
                  }}
                >
                  Parar Robô
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* ── Coluna direita ── */}
        <Grid item xs={12} md={7}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, height: '100%' }}>

            {/* Card Claude AI — em breve */}
            <Card elevation={0} sx={{
              borderRadius: 3, overflow: 'hidden',
              border: '1px solid rgba(124,58,237,0.2)',
              bgcolor: '#fff',
              background: 'linear-gradient(135deg, #faf5ff 0%, #f0f9ff 100%)',
            }}>
              <Box sx={{ height: 4, background: 'linear-gradient(90deg, #7C3AED 0%, #2563EB 100%)' }} />
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                      width: 44, height: 44, borderRadius: 2.5,
                      background: 'linear-gradient(135deg, #7C3AED 0%, #2563EB 100%)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 4px 14px rgba(124,58,237,0.3)',
                    }}>
                      <AutoAwesomeIcon sx={{ color: '#fff', fontSize: 22 }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 800, fontSize: 15, color: '#1E1B4B' }}>
                        Automação com Claude AI
                      </Typography>
                      <Typography sx={{ fontSize: 11.5, color: '#6D28D9' }}>
                        Inteligência artificial para lançamentos automáticos
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label="EM BREVE"
                    size="small"
                    icon={<LockClockIcon sx={{ fontSize: 12, color: '#7C3AED !important' }} />}
                    sx={{ bgcolor: 'rgba(124,58,237,0.1)', color: '#7C3AED', fontWeight: 700, fontSize: 10, height: 24, border: 'none' }}
                  />
                </Box>

                <Typography sx={{ fontSize: 13, color: '#64748B', lineHeight: 1.7, mb: 2.5 }}>
                  O próximo robô vai usar <b style={{ color: '#7C3AED' }}>Claude AI</b> para ler os lançamentos
                  de receita dos dentistas <b>Pessoa Física</b>, acessar o eCAC automaticamente e preencher
                  o <b>Carnê Leão</b> mês a mês — sem intervenção manual.
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <FeatureCard icon={PersonIcon}         title="Lê lançamentos PF"       desc="Busca automaticamente as receitas mensais dos dentistas PF cadastrados"      badge="Planejado" color="#7C3AED" />
                  <FeatureCard icon={SmartToyIcon}        title="Acessa o eCAC"           desc="Navega pelo portal da Receita Federal de forma autônoma e segura"            badge="Planejado" color={BLUE}    />
                  <FeatureCard icon={ReceiptLongIcon}     title="Preenche o Carnê Leão"   desc="Insere os valores mês a mês com validação de dados antes de confirmar"       badge="Planejado" color={GREEN}   />
                  <FeatureCard icon={CheckCircleIcon}     title="Confirma e registra log" desc="Gera relatório do que foi lançado e notifica o dentista por e-mail"          badge="Planejado" color={AMBER}   />
                </Box>
              </CardContent>
            </Card>

            {/* Log de atividade */}
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #E2E8F0', bgcolor: '#fff', flex: 1 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <FiberManualRecordIcon sx={{ fontSize: 10, color: ativo ? GREEN : '#CBD5E1' }} />
                  <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#0F172A' }}>
                    Log de Atividade
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>Últimas {log.length} entradas</Typography>
                </Box>

                <Box sx={{
                  maxHeight: 200, overflowY: 'auto',
                  display: 'flex', flexDirection: 'column', gap: 0.8,
                  '&::-webkit-scrollbar': { width: 4 },
                  '&::-webkit-scrollbar-thumb': { bgcolor: '#E2E8F0', borderRadius: 2 },
                }}>
                  {log.map((entry) => (
                    <Box key={entry.id} sx={{
                      display: 'flex', alignItems: 'flex-start', gap: 1.2,
                      px: 1.5, py: 0.9, borderRadius: 1.5,
                      bgcolor: logBg[entry.tipo] || 'rgba(0,0,0,0.02)',
                    }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: logColor[entry.tipo] || '#94A3B8', mt: 0.7, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: 12, color: '#475569', flex: 1, lineHeight: 1.5 }}>{entry.msg}</Typography>
                      <Typography sx={{ fontSize: 10.5, color: '#94A3B8', flexShrink: 0, mt: 0.2 }}>{entry.hora}</Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>

          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RoboAutomacao;
